import {
  ViewGridIcon,
  ViewListIcon,
  PhotographIcon,
  TemplateIcon,
} from '@heroicons/react/solid';

import { useEffect, useState } from 'react';
import clsxm from '@/lib/clsxm';

import { SimpleTooltip } from '@/components/ui/tooltip';

interface ViewMode {
  layout: 'grid' | 'row';
  size: 'small' | 'medium' | 'large';
  fill: 'aspect-ratio' | 'square';
}

interface ViewModeSelectorProps {
  viewMode: ViewMode | undefined;
  onViewModeChange: (viewMode: ViewMode) => void;
}

export const ViewModeSelector = ({
  viewMode,
  onViewModeChange,
}: ViewModeSelectorProps) => {
  const [showDropdown, setShowDropdown] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.view-dropdown-container')) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showDropdown]);

  return (
    <div className='relative view-dropdown-container'>
      <SimpleTooltip title='View options'>
        <button
          onClick={() => setShowDropdown(!showDropdown)}
          className='p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors'
          aria-label='View options'
        >
          <ViewGridIcon className='h-5 w-5 text-gray-600 dark:text-gray-400' />
        </button>
      </SimpleTooltip>

      {/* Dropdown Menu */}
      {showDropdown && (
        <div className='absolute right-0 mt-1 w-48 rounded-lg shadow-lg bg-white dark:bg-slate-800 border border-gray-200 dark:border-gray-700 z-[1000]'>
          <div className='p-2'>
            <div className='text-xs font-medium text-gray-500 dark:text-gray-400 px-2 py-1'>
              Layout
            </div>
            <button
              onClick={() => {
                onViewModeChange({ ...viewMode!, layout: 'grid' });
                setShowDropdown(false);
              }}
              className={clsxm(
                'w-full flex items-center px-2 py-1.5 text-sm rounded transition-colors',
                viewMode?.layout === 'grid'
                  ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                  : 'hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300',
              )}
            >
              <ViewGridIcon className='h-4 w-4 mr-2' />
              Grid
            </button>
            <button
              onClick={() => {
                onViewModeChange({ ...viewMode!, layout: 'row' });
                setShowDropdown(false);
              }}
              className={clsxm(
                'w-full flex items-center px-2 py-1.5 text-sm rounded transition-colors',
                viewMode?.layout === 'row'
                  ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                  : 'hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300',
              )}
            >
              <ViewListIcon className='h-4 w-4 mr-2' />
              Row
            </button>
          </div>

          <div className='border-t border-gray-200 dark:border-gray-700 p-2'>
            <div className='text-xs font-medium text-gray-500 dark:text-gray-400 px-2 py-1 flex items-center justify-between'>
              <span>Size</span>
              {viewMode?.layout === 'row' && (
                <span className='text-[10px] text-gray-400 dark:text-gray-500'>
                  Grid only
                </span>
              )}
            </div>
            <button
              onClick={() => {
                if (viewMode?.layout !== 'row') {
                  onViewModeChange({ ...viewMode!, size: 'small' });
                  setShowDropdown(false);
                }
              }}
              disabled={viewMode?.layout === 'row'}
              className={clsxm(
                'w-full flex items-center px-2 py-1.5 text-sm rounded transition-colors',
                viewMode?.layout === 'row'
                  ? 'opacity-40 cursor-not-allowed text-gray-400 dark:text-gray-600'
                  : viewMode?.size === 'small'
                    ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300',
              )}
            >
              <span className='w-4 h-4 mr-2 text-xs font-bold'>S</span>
              Small
            </button>
            <button
              onClick={() => {
                if (viewMode?.layout !== 'row') {
                  onViewModeChange({ ...viewMode!, size: 'medium' });
                  setShowDropdown(false);
                }
              }}
              disabled={viewMode?.layout === 'row'}
              className={clsxm(
                'w-full flex items-center px-2 py-1.5 text-sm rounded transition-colors',
                viewMode?.layout === 'row'
                  ? 'opacity-40 cursor-not-allowed text-gray-400 dark:text-gray-600'
                  : viewMode?.size === 'medium'
                    ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300',
              )}
            >
              <span className='w-4 h-4 mr-2 text-xs font-bold'>M</span>
              Medium
            </button>
            <button
              onClick={() => {
                if (viewMode?.layout !== 'row') {
                  onViewModeChange({ ...viewMode!, size: 'large' });
                  setShowDropdown(false);
                }
              }}
              disabled={viewMode?.layout === 'row'}
              className={clsxm(
                'w-full flex items-center px-2 py-1.5 text-sm rounded transition-colors',
                viewMode?.layout === 'row'
                  ? 'opacity-40 cursor-not-allowed text-gray-400 dark:text-gray-600'
                  : viewMode?.size === 'large'
                    ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300',
              )}
            >
              <span className='w-4 h-4 mr-2 text-xs font-bold'>L</span>
              Large
            </button>
          </div>

          <div className='border-t border-gray-200 dark:border-gray-700 p-2'>
            <div className='text-xs font-medium text-gray-500 dark:text-gray-400 px-2 py-1'>
              Fill
            </div>
            <button
              onClick={() => {
                onViewModeChange({ ...viewMode!, fill: 'aspect-ratio' });
                setShowDropdown(false);
              }}
              className={clsxm(
                'w-full flex items-center px-2 py-1.5 text-sm rounded transition-colors',
                viewMode?.fill === 'aspect-ratio'
                  ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                  : 'hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300',
              )}
            >
              <PhotographIcon className='h-4 w-4 mr-2' />
              Aspect Ratio
            </button>
            <button
              onClick={() => {
                onViewModeChange({ ...viewMode!, fill: 'square' });
                setShowDropdown(false);
              }}
              className={clsxm(
                'w-full flex items-center px-2 py-1.5 text-sm rounded transition-colors',
                viewMode?.fill === 'square'
                  ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                  : 'hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300',
              )}
            >
              <TemplateIcon className='h-4 w-4 mr-2' />
              Square
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
